import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch } from "react-router-dom";
import {
  Offcanvas,
  Button,
  InputGroup,
  Form,
} from "react-bootstrap";

import shortid from 'shortid';
import _ from 'lodash';
import cx from 'classnames';

import * as widgetTypes from "constants/widgetTypes";

import { fetchCustomFormFields } from "actions/formFields";
import { updateProgressiveGatingProperty } from "actions/gating";
import { formFieldTypes } from "components/Editor/widgets/form";
import {
  DndContext,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import InfiniteScroll from "components/InfiniteScroll";
import { useDraggableSensors } from "components/Editor/widgets/content/sectionManager/manualSection/dragHelpers";
import { getBaseUrl } from "services/util";

import { deleteWidgets, insertFieldToForm } from "actions/editor";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import styles from './progressiveGating.module.scss';

const AddFieldSection = ({ editorSession }) => {
  const dispatch = useDispatch();

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const loading = useSelector((state) => _.get(state, "formFields.loading") || false);
  const pagination = useSelector((state) => _.get(state, "formFields.pagination") || {});
  const hasMoreItems = pagination.currentPage < pagination.totalPages - 1;
  const workspaceId = useSelector((state) => _.get(state, "workspaces.workspace.item.id") || null);
  const error = useSelector((state) => _.get(state, "formFields.error") || false);
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`));

  const formId = widgets?.find(item => item.type === widgetTypes.FORM)?.uid;

  const customFormFields = useSelector((state) => _.get(state, "formFields.items", null));

  const selectedFields = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    
    const steps = widgets.filter(item => item.type === widgetTypes.FORM_STEP).sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;

      return o1 - o2;
    });

    const step = steps.find((item) => item.name === 'Form')?.uid;
    if (!step) {
      return [];
    }

    const fields = widgets.filter(item => _.includes(formFieldTypes, item.type) && item.parent === step).map(item => _.get(item, 'content.id') || _.get(item, 'content'));
    return fields;
  });

  const [query, setQuery] = useState('');

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    const request = {
      "workspace": workspaceId,
      "defaultFields": true,
      "query": '',
      "ignoreTypes": [],
      "page": nextPage,
      "limit": 50,
      "sort": "created_date"
    }

    if(!error) {
      dispatch(fetchCustomFormFields(request));
    }
  };

  const handleAddCustomField = (data) => {
    const existing = _.includes(selectedFields, data.id);
    if (!existing) {
      dispatch(insertFieldToForm(editorSession, formId, data));
    }
    else {
      const formFieldWidgets = widgets.filter(item => _.includes(formFieldTypes, item.type));
      const deleteWidget = formFieldWidgets.filter(item => (_.get(item,'content.id') || _.get(item, 'content')) === data.id).map(item => item.uid);
      dispatch(deleteWidgets(editorSession, deleteWidget));
    }
  }

  const handleManageCustomFormFields = () => {
    let customFormFieldsURL = `${baseUrl}/integration-settings/form-fields/`;
    const win = window.open(customFormFieldsURL, "_blank");
    win.focus();
  }

  const filteredCustomFields = useMemo(() => {
    const lowerQuery = query.toLowerCase();

    return customFormFields.filter(
      item =>
        item.userDefined &&
        item.name.toLowerCase().includes(lowerQuery)
    );
  }, [customFormFields, query]);

  return (
    <div className={styles.addFieldSection}>
      <div className={styles.defaultFields}>
        <div className={styles.header}>
          <span className={styles.title}>Default Fields</span>
        </div>
        <div className={styles.fieldList}>
          {customFormFields
            .filter((item) => !item.userDefined)
            .map((data) => {
              const checked = _.includes(selectedFields, data.id);
              return (
                <FieldItem
                  key={data.id}
                  data={data}
                  checked={checked}
                  handleAddCustomField={() => handleAddCustomField(data)}
                />
              );
            })}
        </div>
      </div>
      <div className={styles.customFields}>
        <div className={styles.header}>
          <span className={styles.title}>Custom Fields</span>
          <Button variant="info" onClick={() => handleManageCustomFormFields()}>
            <i
              className={cx(
                "cleverstory-story-settings",
                styles.storySettingsIcon
              )}
            />
          </Button>
        </div>
        <div className={styles.fieldList}>
          <div className={styles.searchField}>
            <Form.Label className={styles.label}>
              <i className={cx(styles.icon, "cleverstory-search")} />
            </Form.Label>
            <InputGroup className={styles.inputGroup}>
              <Form.Control
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
              />
            </InputGroup>
          </div>
          <InfiniteScroll
            className={styles.scrollable}
            loading={loading}
            hasMore={hasMoreItems}
            hideLoadMoreButton={true}
            loadMore={() => {
              loadMore();
            }}
          >
            {filteredCustomFields.length === 0 && !loading && (
              <div className={styles.noCustomFields}>
                <p>No custom fields found</p>
                <span>Add one to collect more details from your visitors.</span>
              </div>
            )}
            {filteredCustomFields?.map((data) => {
              const checked = _.includes(selectedFields, data.id);
              return (
                <FieldItem
                  key={data.id}
                  data={data}
                  checked={checked}
                  handleAddCustomField={() => handleAddCustomField(data)}
                />
              );
            })}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
}

const RenderIcon = ( {fieldType} ) => {
  switch (fieldType) {
    case 'string_field':
      return <i className='cleverstory-text-layer'/>
        
    case 'list_field':
      return <i className='cleverstory-list'/>
        
    case 'number_field':
      return <i className='cleverstory-hash'/>
        
    case 'boolean_field':
      return <i className='cleverstory-checkbox'/>
        
    default:
      return <i className='cleverstory-list'/>
  }
}

const FieldItem = ({ handleAddCustomField, data, checked }) => {
  return (
    <div className={cx(styles.customFormField, { [styles.disabled]: data.uid === 'email'})}>
      <input 
        type="checkbox"
        checked={checked}
        className={cx(styles.customCheckbox, {[styles.checked]: checked})}
        onClick={handleAddCustomField}
      />
      <span className={cx(styles.field, {[styles.checked]: checked})}>
        <RenderIcon fieldType={_.get(data, 'type', null)} />
        <TruncatedTextWithTooltip content={data.name} maxWidth={180} />
      </span>
    </div>
  )
}

const SortableField = ({ field, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString({
        ...transform,
        scaleX: 1,
        scaleY: 1,
      }),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="field-item"
    >
      <PriorityFieldItem field={field} onDelete={onDelete} attributes={attributes} listeners={listeners} isDragging={isDragging}/>
    </div>
  );
};

const PriorityFieldSection = React.forwardRef(({ editorSession }, ref) => {
  return (  
    <div className={styles.priorityFieldSection}>
      <div className={styles.header}>
        <span className={styles.title}>Priority Fields</span>
        <span className={styles.description}>
          Group and set the order in which visitor details are collected. These fields will be asked step by step as users engage with your content.
        </span>
      </div>
      <div className={styles.sectionContainer}>
        <DraggableFieldsContainers ref={ref} editorSession={editorSession} />
      </div>
    </div>
  )
});

const PriorityFieldItem = ({ field, index, onDelete, attributes, listeners, isDragging }) => {

  return (
      <div key={index} className={cx(styles.fieldItem, 'field-draggin')}>
        <div className={styles.fieldContent}>
          <span style={{ cursor: isDragging ? 'grabbing' : 'grab' }} className={styles.fieldDragHandle} {...attributes} {...listeners}>
            <i className="cleverstory-drag"/>
          </span>
          <span className={styles.fieldLabel}>
            <TruncatedTextWithTooltip content={field.label} maxWidth={280} />
          </span>
        </div>
        {field.uid !== 'email' &&
          <Button variant="info" className={styles.deleteButton} onClick={() => {
            onDelete(field.id);
          }}>
              <i className="cleverstory-trash" />
          </Button>
        }
      </div>
  )
}

// ========== MAIN COMPONENT ==========
const DraggableFieldsContainers = React.forwardRef(({ editorSession }, ref) => {

  const { id } = useParams();

  const dispatch = useDispatch();

  const [_activeField, setActiveField] = useState(null);

  const customFormFields = useSelector((state) => _.get(state, "formFields.items", null));
  const progressiveGatingClusters = useSelector(state => _.get(state, `gatings.gating.${id}.item.progressiveGatingClusters`, [])) || [];

  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`));
  const selectedFields = useMemo(() => {
    if (!widgets?.length) return [];
  
    const steps = widgets
      .filter(item => item.type === widgetTypes.FORM_STEP)
      .sort((a, b) => (a.order ?? 1) - (b.order ?? 1));
  
    const stepUid = steps.find(item => item.name === 'Form')?.uid;
    if (!stepUid) return [];
  
    const fields = widgets
      .filter(
        item =>
          formFieldTypes.includes(item.type) &&
          item.parent === stepUid
      )
      .sort((a, b) => (a.order ?? 1) - (b.order ?? 1)) // ✅ important
      .map(item => item.content?.id ?? item.content);
  
    return fields;
  }, [widgets]);
  
  const defaultContainerIdRef = useRef(
    `container_${shortid.generate()}`
  );

  const fieldMap = customFormFields.reduce((acc, field) => {
    acc[field.id] = field;
    return acc;
  }, {});

  const mappedClusters = useMemo(() => {
    if (!progressiveGatingClusters?.length) {
      return [
        {
          id: defaultContainerIdRef.current,
          fields: [],
          order: 1,
        },
      ];
    }

    return progressiveGatingClusters.map(cluster => {
      const mappedFields = Array.isArray(cluster.fields)
        ? cluster.fields
            .map(f => {
              const fieldId = typeof f === 'string' ? f : f?.id;
              const field = customFormFields.find(cf => cf?.id === fieldId);

              if (!field) return null;

              return {
                id: field.id,
                uid: field.uid,
                label: field.name,
                type: field.type,
              };
            })
            .filter(Boolean)
        : [];

      return {
        ...cluster,
        fields: mappedFields,
      };
    });
  }, [progressiveGatingClusters, customFormFields]);

  const sensors = useDraggableSensors({ delay: 50 });
  
  useEffect(() => {
    const allFieldsEmpty = progressiveGatingClusters?.every(cluster => cluster.fields.length === 0);

    if (!progressiveGatingClusters?.length || (selectedFields?.length && allFieldsEmpty)) {
      let emptyContainer = [{
        id: defaultContainerIdRef.current,
        fields: selectedFields?.length ? selectedFields : [],
        order: 1,
      }];

      dispatch(updateProgressiveGatingProperty(id, {
        progressiveGatingClusters: emptyContainer
      }));
    }
  }, [selectedFields, progressiveGatingClusters]);

  useEffect(() => {
    // Clone clusters to avoid mutation
    let updatedClusters = mappedClusters.map(cluster => ({
      ...cluster,
      fields: [...cluster.fields],
    }));

    // Collect all existing field IDs in clusters
    const existingFieldIds = new Set(
      updatedClusters.flatMap(cluster =>
        cluster.fields.map(field => field.id)
      )
    );

    /* -------------------------------
      ADD newly selected fields
    --------------------------------*/
    const allFieldsEmpty = updatedClusters?.every(cluster => cluster.fields.length === 0);

    selectedFields.forEach(fieldId => {
      if (!existingFieldIds.has(fieldId)) {
        const field = fieldMap[fieldId];

        if (!field) return;

        let index = 0;
        
        if(!allFieldsEmpty) {
          index = updatedClusters.length - 1;
        }

        updatedClusters[index]?.fields.push({
          id: field.id,
          uid: field.uid,
          label: field.label,
          type: field.type,
        });
      }
    });

    /* -------------------------------
      REMOVE unselected fields
    --------------------------------*/
    updatedClusters = updatedClusters.map(cluster => ({
      ...cluster,
      fields: cluster.fields.filter(field =>
        selectedFields.includes(field.id)
      ),
    }));

    dispatch(updateProgressiveGatingProperty(id, {
      progressiveGatingClusters: allFieldsEmpty ? [] : updatedClusters
    }));
  }, [selectedFields]);

  // Find which container a field belongs to
  const findContainer = (fieldId) => {
    return mappedClusters.find(container =>
      container.fields.some(field => field.id === fieldId)
    );
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    const container = findContainer(active.id);
    const field = container?.fields.find(f => f.id === active.id);
    setActiveField(field);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    
    // Find which containers the active and over items belong to
    const activeContainer = mappedClusters.find(c => 
      c.fields.some(f => f.id === activeId)
    );
    
    // Check if overId is a container id OR a field id
    const overContainer = mappedClusters.find(c => 
      c.id === overId || c.fields.some(f => f.id === overId)
    );
    
    if (!activeContainer || !overContainer) return;
    if (activeContainer.id === overContainer.id) return; // Same container
    
    // Move item between containers
    const newClusters = mappedClusters.map(container => {
      if (container.id === activeContainer.id) {
        // Remove from source
        return {
          ...container,
          fields: container.fields.filter(f => f.id !== activeId)
        };
      }
      if (container.id === overContainer.id) {
        // Add to destination
        const fieldToMove = activeContainer.fields.find(f => f.id === activeId);
        
        // If dropping on empty container, add at the end
        // If dropping on a field, insert at that position
        const overIndex = container.fields.findIndex(f => f.id === overId);
        const insertIndex = overIndex === -1 ? container.fields.length : overIndex;
        
        const newFields = [...container.fields];
        newFields.splice(insertIndex, 0, fieldToMove);
        
        return {
          ...container,
          fields: newFields
        };
      }
      return container;
    });

    dispatch(updateProgressiveGatingProperty(id, {
      progressiveGatingClusters: newClusters
    }));
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveField(null);
      return;
    }

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);

    if (!activeContainer || !overContainer) {
      setActiveField(null);
      return;
    }

    const activeIndex = activeContainer.fields.findIndex(f => f.id === active.id);
    const overIndex = overContainer.fields.findIndex(f => f.id === over.id);

    if (activeContainer.id === overContainer.id) {
      // Reorder within same container
      const newClusters = mappedClusters.map(container => {
        if (container.id === activeContainer.id) {
          return {
            ...container,
            fields: arrayMove(container.fields, activeIndex, overIndex),
          };
        }
        return container;
      });

      dispatch(updateProgressiveGatingProperty(id, {
        progressiveGatingClusters: newClusters
      }));
    } else {
      // Move to different container
      const activeField = activeContainer.fields[activeIndex];
      
      const newClusters = mappedClusters.map(container => {
        if (container.id === activeContainer.id) {
          // Remove from source
          return {
            ...container,
            fields: container.fields.filter(f => f.id !== active.id),
          };
        } else if (container.id === overContainer.id) {
          // Add to target
          const newFields = [...container.fields];
          newFields.splice(overIndex, 0, activeField);
          return {
            ...container,
            fields: newFields,
          };
        }
        return container;
      });

      dispatch(updateProgressiveGatingProperty(id, {
        progressiveGatingClusters: newClusters
      }));
    }

    setActiveField(null);
  };

  // Delete field from container
  const handleDeleteField = (_containerId, fieldId) => {
    const formFieldWidgets = widgets.filter(item => _.includes(formFieldTypes, item.type));
    const deleteWidget = formFieldWidgets.filter(item => (_.get(item,'content.id') || _.get(item, 'content')) === fieldId).map(item => item.uid);
    dispatch(deleteWidgets(editorSession, deleteWidget));
  };

  // Add new container below specified container
  const handleAddContainer = (afterContainerId) => {
    const index = mappedClusters.findIndex(c => c.id === afterContainerId);

    if(mappedClusters[index]?.fields?.length === 0 || mappedClusters[index + 1]?.fields?.length === 0) {
      return;
    }

    const newContainerId = `container_${shortid.generate()}`;
    const newContainer = {
      id: newContainerId,
      order: mappedClusters[index]?.order + 1 || 1,
      fields: [],
    };

    const newContainers = [...mappedClusters];
    newContainers.splice(index + 1, 0, newContainer);

    dispatch(updateProgressiveGatingProperty(id, {
      progressiveGatingClusters: newContainers
    }));
  };

  return (
    <div className={styles.dragContainer}>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className={styles.containerList}>
          {mappedClusters.map((container) => (
            <Container
              key={container.id}
              container={container}
              onDeleteField={handleDeleteField}
              onAddContainer={handleAddContainer}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
});


const Container = ({ container, onDeleteField, onAddContainer }) => {
  const { setNodeRef } = useDroppable({ 
    id: container.id 
  });
  
  return (
    <div className={styles.containerWrapper}>
      <div 
        className={styles.containerDropzone}
        ref={setNodeRef}  // ← Add this ref!
      >
        <SortableContext
          id={container.id}
          items={container.fields.map((f) => f.id)} // Works for both empty and filled
          strategy={verticalListSortingStrategy}
        >
          {container.fields.length === 0 ? (
            <div className={styles.emptyContainer}></div>
          ) : (
            container.fields.map((field) => (
              <SortableField
                key={field.id}
                field={field}
                onDelete={(fieldId) => onDeleteField(container.id, fieldId)}
              />
            ))
          )}
        </SortableContext>
      </div>

      <div className={styles.addContainerButtonWrapper}>
        <Button
          variant="info"
          className={styles.addContainerButton}
          onClick={() => onAddContainer(container.id)}
        >
          <i className="cleverstory-plus" />
        </Button>
      </div>
    </div>
  );
};

export const ProgressiveGatingSetup = ({ onClose, editorSession, selected})=> {
  const [offset, setOffset] = useState(76);
  const [showFields, setShowFields] = useState(false);

  const breakpoint = useSelector((state) => _.get(state, `${editorSession}.present.breakpoint`));

  const progressiveFieldRef = useRef(null);

  useEffect(() => {
    if(breakpoint === 'mobile') {
      onClose?.();
    }
  }, [breakpoint]);

  useLayoutEffect(() => {
    if(selected){
      const selectedButton = document.getElementById(`button-palette`);
      if (selectedButton) {
        const rect = selectedButton.getBoundingClientRect();
        const offsetValue = rect.y + rect.height;
        setOffset(offsetValue);
      }
    }
  }, [selected]);

  const toggleFields = (value) => {
    setShowFields(value);
  };

  return (
    <Offcanvas
      className={cx(styles.progressiveGatingOffcanvas, {
        [styles.showFields]: showFields
      })}
      show={selected === 'progressive-gating'}
      backdrop={false}
      enforceFocus={false}
      autoFocus={false}
      restoreFocus={false}
      keyboard={true}
      placement="end"
      onHide={onClose}
      style={{
        top: offset,
        width: showFields ? '689px' : '415px',
      }}
    >
      {selected === 'progressive-gating' &&
       <>
        <Offcanvas.Header>
          <Offcanvas.Title className={styles.title}>Progressive Gating</Offcanvas.Title>
          <div className={styles.actions}>
            <Button variant="primary" onClick={()=>{toggleFields(true)}}>
              Add Field
            </Button>
            <Button className="close" variant="info" onClick={()=>{ showFields ? toggleFields(false) : onClose()}}>
              <i className={cx(styles.closeIcon, "cleverstory-close")} />
            </Button>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          { showFields && 
            <AddFieldSection onClose={()=>{toggleFields(false)}} showFields={showFields} editorSession={editorSession} />
          }

          {/* Priority Field Section */}
          <PriorityFieldSection ref={progressiveFieldRef} editorSession={editorSession} />
        </Offcanvas.Body>
        </>
      }
    </Offcanvas>
  );
}